import {
  type ChargeItemDefinitionApplicabilityConditions,
  type CoverageIdentifiers,
  createChargeItemDefinitionApplicability,
  createCodeableConcept,
  createMapWithId,
  createReference,
  getChargeItemDefinitionApplicabilityConditions,
  getFirstCodeFromCodableConcepts,
  mapFhirToModelPeriod,
  system,
  valsOrNone,
} from '@quromedical/fhir-common';
import { compact, first } from '@quromedical/utils';
import type { ClaimChargeLineQuantity } from '../claim/schema';
import type { ClaimTemplateChargeLineQuantity } from '../claim-template/schema';
import type { WithId } from '../types';

import { mapFhirToMoney } from './money';
import type {
  ChargeItemDefinition,
  ChargeLine,
  ChargeLineApplicability,
  PartialChargeItemDefinition,
} from './schema';

const createApplicabilityConditions = (
  applicability: ChargeLineApplicability,
): Partial<
  ChargeItemDefinitionApplicabilityConditions<keyof ChargeLineApplicability>
> => {
  const { administratorCode, schemeCode, description } = applicability;

  return {
    administratorCode: administratorCode
      ? {
          type: 'equal',
          value: administratorCode,
          description,
        }
      : undefined,
    schemeCode: schemeCode
      ? {
          type: 'equal',
          value: schemeCode,
          description,
        }
      : undefined,
  };
};

const mapChargeLineToPropertyGroup = (
  chargeLine: ChargeLine,
): fhir4.ChargeItemDefinitionPropertyGroup => {
  const { amount, applicability, code, type } = chargeLine;

  const conditions = createApplicabilityConditions(applicability);

  return {
    applicability: createChargeItemDefinitionApplicability(conditions),
    priceComponent: [
      {
        type,
        code: createCodeableConcept(code),
        factor: amount.factor,
        amount: {
          currency: amount.currency,
          value: amount.value,
        },
      },
    ],
  };
};

/**
 * The URL doesn't matter too much to us however it must be unique as per FHIR
 * This will prevent creation of duplicate URLs being created
 */
const getChargeItemDefinitionUrl = (title: string): string =>
  `${system}/ChargeItemDefinition/${encodeURIComponent(title)}`;

export const mapChargeItemDefinitionToFhir = (
  model: ChargeItemDefinition,
): fhir4.ChargeItemDefinition => {
  const {
    chargeLines,
    code,
    date,
    description,
    title,
    status,
    effectivePeriod,
  } = model;

  return {
    resourceType: 'ChargeItemDefinition',
    status,
    title,
    description,
    date,
    code: createCodeableConcept(code),
    url: getChargeItemDefinitionUrl(title),
    propertyGroup: valsOrNone(chargeLines.map(mapChargeLineToPropertyGroup)),
    effectivePeriod,
  };
};

export const mapChargeItemDefinitionToFhirWithID = createMapWithId(
  mapChargeItemDefinitionToFhir,
);

const getApplicabilityFromFhir = (
  applicability: fhir4.ChargeItemDefinitionApplicability[] = [],
): ChargeLineApplicability => {
  const conditions =
    getChargeItemDefinitionApplicabilityConditions<
      keyof ChargeLineApplicability
    >(applicability);

  return {
    administratorCode: conditions.administratorCode?.value,
    schemeCode: conditions.schemeCode?.value,
    description:
      conditions.schemeCode?.description ||
      conditions.administratorCode?.description,
  };
};

const mapPropertyGroupToChargeLine = (
  propertyGroup: fhir4.ChargeItemDefinitionPropertyGroup,
): Partial<ChargeLine> | undefined => {
  const priceComponent = first(propertyGroup.priceComponent);

  const amount = mapFhirToMoney(priceComponent?.factor, priceComponent?.amount);

  const type = priceComponent?.type;
  const code = getFirstCodeFromCodableConcepts(compact([priceComponent?.code]));
  const applicability = getApplicabilityFromFhir(propertyGroup.applicability);

  return {
    amount,
    applicability,
    code,
    type,
  };
};

export const mapChargeItemDefinitionToModel = (
  fhir: WithId<fhir4.ChargeItemDefinition>,
): WithId<PartialChargeItemDefinition> => {
  const {
    id,
    status,
    date,
    code,
    title,
    description,
    propertyGroup = [],
    effectivePeriod,
  } = fhir;
  const period = mapFhirToModelPeriod(effectivePeriod);

  return {
    id,
    status,
    date,
    title,
    description,
    code: getFirstCodeFromCodableConcepts(compact([code])),
    chargeLines: compact(propertyGroup.map(mapPropertyGroupToChargeLine)),
    effectivePeriod: period,
  };
};

export const getApplicableChargeLine = (
  model: PartialChargeItemDefinition,
  coverageIdentifiers: CoverageIdentifiers,
): Partial<ChargeLine> | undefined =>
  model.chargeLines.find(
    (line) => line.applicability?.schemeCode === coverageIdentifiers.schemeCode,
  );

/**
 * Gets charge lines from charge item definition with the applied applicability. A single charge
 * line should only one applicability at most
 */
export const mapChargeItemDefinitionToChargeLine = (
  coverageIdentifiers: CoverageIdentifiers,
  chargeLines: ClaimTemplateChargeLineQuantity[],
  chargeItem: WithId<PartialChargeItemDefinition>,
): ClaimChargeLineQuantity | undefined => {
  const quantity = chargeLines.find(
    (line) => line.chargeLine === chargeItem.id,
  );
  const chargeLine = getApplicableChargeLine(chargeItem, coverageIdentifiers);

  const name = chargeItem.title;

  const { amount, code } = chargeLine || {};

  const hasAllData = quantity && amount && code && name;

  if (!hasAllData) {
    return undefined;
  }

  const chargeItemRef = createReference(
    'ChargeItemDefinition',
    chargeItem.id,
    chargeItem.title,
  );

  return {
    chargeItem: chargeItemRef,
    chargeLine: {
      code,
      amount,
      name,
    },
    quantity: quantity?.quantity,
  };
};
